import { useState, useEffect, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, Grid, Stack,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import UploadIcon from '@mui/icons-material/Upload';
import DrawIcon from '@mui/icons-material/Draw';
import { useNavigate } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DrawingPad from './DrawingPad';

type DrawMode = 'signature' | 'initials' | null;

const MyProfilePage: React.FC = () => {
  const { userProfileApiWrapper }: Api = useApi();
  const toast = useToastMessageQueue();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [initials, setInitials] = useState('');
  const [title, setTitle] = useState('');

  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [initialsUrl, setInitialsUrl] = useState<string | null>(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [uploadingInitials, setUploadingInitials] = useState(false);

  const [drawMode, setDrawMode] = useState<DrawMode>(null);

  const signatureInputRef = useRef<HTMLInputElement>(null);
  const initialsInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [profileRes, sigRes, initRes] = await Promise.allSettled([
          userProfileApiWrapper.getMyProfile(),
          userProfileApiWrapper.getSignatureUrl(),
          userProfileApiWrapper.getInitialsUrl(),
        ]);

        if (profileRes.status === 'fulfilled') {
          const p = profileRes.value.data?.data;
          if (p) {
            setFirstName(p.firstName ?? '');
            setLastName(p.lastName ?? '');
            setInitials(p.initials ?? '');
            setTitle(p.title ?? '');
          }
        }
        if (sigRes.status === 'fulfilled') setSignatureUrl(sigRes.value.data?.data ?? null);
        if (initRes.status === 'fulfilled') setInitialsUrl(initRes.value.data?.data ?? null);
      } catch (err) {
        handleAPIError(err, toast, errors);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await userProfileApiWrapper.upsertMyProfile('1', { firstName, lastName, initials, title });
      toast.success({ header: intl.get('common.success') || 'Saved', body: intl.get('profile.saved') || 'Profile updated.' });
    } catch (err) {
      handleAPIError(err, toast, errors);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadSignature = async (file: File | Blob) => {
    setUploadingSignature(true);
    setDrawMode(null);
    try {
      const f = file instanceof File ? file : new File([file], 'signature.png', { type: 'image/png' });
      const res = await userProfileApiWrapper.uploadSignature('1', f);
      setSignatureUrl(res.data?.data ?? null);
      toast.success({ header: intl.get('common.success') || 'Saved', body: intl.get('profile.signatureUploaded') || 'Signature uploaded.' });
    } catch (err) {
      handleAPIError(err, toast, errors);
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleUploadInitials = async (file: File | Blob) => {
    setUploadingInitials(true);
    setDrawMode(null);
    try {
      const f = file instanceof File ? file : new File([file], 'initials.png', { type: 'image/png' });
      const res = await userProfileApiWrapper.uploadInitialsImage('1', f);
      setInitialsUrl(res.data?.data ?? null);
      toast.success({ header: intl.get('common.success') || 'Saved', body: intl.get('profile.initialsUploaded') || 'Initials image uploaded.' });
    } catch (err) {
      handleAPIError(err, toast, errors);
    } finally {
      setUploadingInitials(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <Box className="my-3" sx={{ width: '100%', p: 2 }}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Button startIcon={<KeyboardBackspaceIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          {intl.get('common.back') || 'Back'}
        </Button>

        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
          {intl.get('profile.pageTitle') || 'My Profile'}
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={intl.get('profile.firstName') || 'First Name'}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={intl.get('profile.lastName') || 'Last Name'}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={intl.get('profile.initials') || 'Initials'}
              value={initials}
              onChange={(e) => setInitials(e.target.value)}
              inputProps={{ maxLength: 10 }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={intl.get('profile.title') || 'Title'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? intl.get('common.saving') || 'Saving…' : intl.get('common.save') || 'Save'}
          </Button>
        </Box>
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography color="primary" variant="h4" sx={{ mb: 3 }}>
          {intl.get('profile.signatures') || 'Signatures'}
        </Typography>

        <Grid container spacing={4}>
          {/* Full signature */}
          <Grid size={{ xs: 12, sm: 6 }}>
            {drawMode === 'signature' ? (
              <DrawingPad
                label={intl.get('profile.signature') || 'Signature'}
                onSave={(blob) => handleUploadSignature(blob)}
                onCancel={() => setDrawMode(null)}
              />
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {intl.get('profile.signature') || 'Signature'}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  {signatureUrl
                    ? <img src={signatureUrl} alt="signature" style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }} />
                    : <Typography variant="body2" color="text.secondary">{intl.get('profile.noSignature') || 'No signature uploaded'}</Typography>
                  }
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<DrawIcon />}
                    onClick={() => setDrawMode('signature')}
                    size="small"
                  >
                    {intl.get('profile.draw') || 'Draw'}
                  </Button>
                  <input
                    ref={signatureInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadSignature(f); e.target.value = ''; }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => signatureInputRef.current?.click()}
                    disabled={uploadingSignature}
                    size="small"
                  >
                    {uploadingSignature ? intl.get('common.uploading') || 'Uploading…' : intl.get('profile.uploadSignature') || 'Upload'}
                  </Button>
                </Stack>
              </>
            )}
          </Grid>

          {/* Initials image */}
          <Grid size={{ xs: 12, sm: 6 }}>
            {drawMode === 'initials' ? (
              <DrawingPad
                label={intl.get('profile.initialsImage') || 'Initials Image'}
                onSave={(blob) => handleUploadInitials(blob)}
                onCancel={() => setDrawMode(null)}
              />
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {intl.get('profile.initialsImage') || 'Initials Image'}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  {initialsUrl
                    ? <img src={initialsUrl} alt="initials" style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }} />
                    : <Typography variant="body2" color="text.secondary">{intl.get('profile.noInitials') || 'No initials image uploaded'}</Typography>
                  }
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<DrawIcon />}
                    onClick={() => setDrawMode('initials')}
                    size="small"
                  >
                    {intl.get('profile.draw') || 'Draw'}
                  </Button>
                  <input
                    ref={initialsInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.svg,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadInitials(f); e.target.value = ''; }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => initialsInputRef.current?.click()}
                    disabled={uploadingInitials}
                    size="small"
                  >
                    {uploadingInitials ? intl.get('common.uploading') || 'Uploading…' : intl.get('profile.uploadInitials') || 'Upload'}
                  </Button>
                </Stack>
              </>
            )}
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default MyProfilePage;
